import { useEffect } from 'react';
import { EquipmentItem } from '@/backend/models/equipmentItem';
import { nativeBridge } from '@/frontend/embed/nativeBridge';
import { useInstrumentStore } from '@/frontend/store/InstrumentStore';
import { useProcedureInstanceStore } from '@/frontend/store/ProcedureInstanceStore';
import {
  DESKTOP_INSTRUMENT_TYPE,
  DesktopInstrument,
  needsEngineConnection,
} from '@/types/instrumentBridgeTypes';
import { INSTRUMENT_CATEGORY } from '@/types/equipmentTypes';

/**
 * Everything one equipment item needs to be assigned an instrument and
 * connected, so the card on an operation and the cell in the equipment table
 * behave the same without saying it twice.
 *
 * Asking desktop for its instruments happens here, guarded on `loaded`, so
 * however many of these are mounted only the first one asks.
 */

export interface EquipmentConnectionState {
  /** False in an ordinary browser, where there is no desktop app to ask. */
  framed: boolean;
  /** Narrowed to what suits this equipment item; see `instrumentsToShow`. */
  instruments: DesktopInstrument[];
  assignedId?: string;
  assigned?: DesktopInstrument;
  connected: boolean;
  busy: boolean;
  error?: string;
  /** False once an assigned instrument needs no connection -- manual control. */
  connectable: boolean;
  /** Connected, or assigned something that needs no connection. */
  ready: boolean;
  canToggle: boolean;
  /** What this connection was last given, so the fields come back filled in. */
  storedConfiguration?: string;
  start: () => void;
  /**
   * Stop is its own action rather than "whichever start is not": an errored
   * provider shows Stop, and desktop's Stop there asks the engine to drop it.
   */
  stop: () => void;
  /** Record the choice and its configuration, leaving the provider alone. */
  saveWith: (instrumentId: string, configuration: string) => void;
  /** Record it and start the provider. */
  startWith: (instrumentId: string, configuration: string) => void;
}

export function useEquipmentConnection(equipment: EquipmentItem): EquipmentConnectionState {
  const procedure = useProcedureInstanceStore(state => state.procedureInstance);
  const instruments = useInstrumentStore(state => state.instruments);
  const loaded = useInstrumentStore(state => state.loaded);
  const connected = useInstrumentStore(state => state.connectedEquipmentIds.includes(equipment.id));
  const assignedId = useInstrumentStore(state => state.instrumentIdByEquipmentId[equipment.id]);
  const busy = useInstrumentStore(state => state.busyEquipmentIds.includes(equipment.id));
  // A row's own failure first; failing to reach desktop at all applies to every row.
  const error = useInstrumentStore(state => state.errorByEquipmentId[equipment.id] || state.loadError);
  const storedConfiguration = useInstrumentStore(state => state.configurationByEquipmentId[equipment.id]);
  const load = useInstrumentStore(state => state.load);
  const save = useInstrumentStore(state => state.save);
  const connect = useInstrumentStore(state => state.connect);
  const disconnect = useInstrumentStore(state => state.disconnect);

  const framed = nativeBridge.isAvailable();
  const procedureId = procedure?.id;

  useEffect(() => {
    if (!framed || loaded || !procedureId) return;
    void load(procedureId);
  }, [framed, loaded, procedureId, load]);

  // Resolved against every instrument, not the narrowed list: an assignment
  // made before a category changed should still name its instrument rather
  // than silently reading as unassigned.
  const assigned = instruments.find(({ id }) => id === assignedId);
  const offered = instrumentsToShow(instruments, equipment.category, assigned);
  // Before anything is assigned the control still shows, disabled: appearing
  // only after a choice would make the row jump.
  const connectable = !assigned || needsEngineConnection(assigned);
  const ready = connected || (Boolean(assigned) && !needsEngineConnection(assigned));

  return {
    framed,
    instruments: offered,
    assignedId,
    assigned,
    connected,
    busy,
    error,
    connectable,
    ready,
    canToggle: Boolean(assignedId) && Boolean(procedureId) && !busy,
    storedConfiguration,
    start: () => {
      if (!procedureId) return;
      void connect(equipment, procedureId);
    },
    stop: () => {
      if (!procedureId) return;
      void disconnect(equipment, procedureId);
    },
    saveWith: (instrumentId: string, configuration: string) => {
      if (!procedureId) return;
      void save(equipment, procedureId, instrumentId, configuration);
    },
    startWith: (instrumentId: string, configuration: string) => {
      if (!procedureId) return;
      // One call: desktop records the assignment before it starts the provider.
      void connect(equipment, procedureId, configuration, instrumentId);
    },
  };
}

/**
 * Which instruments suit an equipment item.
 *
 * Ported from veriform-desktop's `instrumentsToShow` (Equipment/
 * ConnectionModal.tsx:115). Only VISA instruments are narrowed: a camera,
 * a software instrument or manual control is always offered, whatever the
 * equipment item says it is.
 *
 * A VISA instrument shows when it has no category of its own, when it matches
 * the equipment item, or when the equipment item has no category -- so an
 * uncategorised item on either side never hides anything.
 *
 * The one addition is keeping an already-assigned instrument in the list even
 * when it would be filtered out. Desktop picks from a separate dialog; this is
 * a dropdown, and dropping the assigned option would leave it showing blank.
 */
function instrumentsToShow(
  instruments: DesktopInstrument[],
  equipmentCategory: INSTRUMENT_CATEGORY | undefined,
  assigned: DesktopInstrument | undefined,
): DesktopInstrument[] {
  const category = equipmentCategory || INSTRUMENT_CATEGORY.NONE;

  return instruments.filter(instrument => {
    if (instrument.id === assigned?.id) return true;
    if (instrument.instrumentType !== DESKTOP_INSTRUMENT_TYPE.VISA) return true;

    return instrument.category === INSTRUMENT_CATEGORY.NONE
      || instrument.category === category
      || category === INSTRUMENT_CATEGORY.NONE;
  });
}
